import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { StatusIndicator } from '@/components/ui/status-indicator';
import { Button } from '@/components/ui/button';
import { DataTableEnhanced } from '@/components/ui/data-table-enhanced';
import type { ColumnDef, ColumnSort } from '@/components/ui/data-table-enhanced';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import {
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  ChevronUp,
  ChevronDown,
  DollarSign,
  Info,
  ExternalLink,
  Download,
  Loader2,
  Search,
  X,
  Percent,
  TriangleAlert,
  Check,
  ChevronsUpDown,
} from 'lucide-react';
import { CompactKpiCard } from '@/components/kpi-card';
import { Input } from '@/components/ui/input';
import PageHeaderBlock from '@/registry/evo-shadcn/blocks/page-header';
import { SensitiveData } from '@/components/sensitive-data';
import { fetchProfitabilityDataPaged } from '@/lib/data-service';
import type { ProfitabilityReportParams } from '@/lib/data-service';
import { getBudgetHealthThresholds } from '@/lib/budget-health-thresholds-storage';
import { getFinanceSettings } from '@/lib/finance-settings-storage';
import { GP_METHOD_LABELS } from '@/lib/gp-calculation';
import { formatCurrency, formatPercent, healthToVariant } from '@/lib/utils';
import { ROUTES } from '@/routes';
import type { ProjectProfitabilityData, PortfolioSummaryDto, ProfitabilityFilterOptions } from '@/types/reports.types';
import { PROJECT_STATUS_LABELS } from '@/types';
import type { PaginationMetadata } from '@/types/pagination';
import { DEFAULT_PAGE_SIZE } from '@/types/pagination';
import { PaginationControls } from '@/components/pagination-controls';
import { useCompany } from '@/contexts/CompanyContext';
import { useErrorHandler } from '@/hooks/use-error-handler';
import { exportProfitabilityToExcel } from '@/lib/export-profitability-excel';
import { toast } from 'sonner';

/** Full set of selectable status names — this report filters by status name, not id */
const STATUS_FILTER_OPTIONS = Object.values(PROJECT_STATUS_LABELS);

const DEFAULT_STATUS_FILTER = PROJECT_STATUS_LABELS[2];

// ─────────────────────────────────────────────────────────────────────────────
// Helper utilities
// ─────────────────────────────────────────────────────────────────────────────

/** Format a variance value with explicit + / − prefix */
function formatVariance(value: number): string {
  if (value > 0) return `+${formatCurrency(value)}`;
  return formatCurrency(value);
}

/** Format a margin variance as percentage-point string, e.g. "+3.2 pp" */
function formatPP(value: number): string {
  const abs = Math.abs(value);
  const sign = value >= 0 ? '+' : '−';
  return `${sign}${abs.toFixed(1)} pp`;
}

/** Return Tailwind colour classes for revenue-polarity variances (positive = good) */
function revenueVarianceClass(variance: number): string {
  if (variance > 0) return 'text-success-600';
  if (variance < 0) return 'text-error-600';
  return 'text-muted-foreground';
}

/** Return Tailwind colour classes for cost-polarity variances (positive = bad) */
function costVarianceClass(variance: number): string {
  if (variance > 0) return 'text-error-600';
  if (variance < 0) return 'text-success-600';
  return 'text-muted-foreground';
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

function ProjectTypeBadge({ type }: { type: string }) {
  if (type === 'fixed-price')
    return <Badge variant="default" className="text-xs whitespace-nowrap">Fixed Fee</Badge>;
  if (type === 'retainer')
    return <Badge variant="secondary" className="text-xs whitespace-nowrap">Retainer</Badge>;
  return <Badge variant="outline" className="text-xs whitespace-nowrap">T&M</Badge>;
}

function RAGBadge({ margin, amberThreshold, redThreshold }: {
  margin: number;
  amberThreshold: number;
  redThreshold: number;
}) {
  if (margin < redThreshold)
    return <Badge variant="destructive">{formatPercent(margin)}</Badge>;
  if (margin < amberThreshold)
    return <Badge variant="warning">{formatPercent(margin)}</Badge>;
  return <Badge variant="success">{formatPercent(margin)}</Badge>;
}

function HealthBadge({ health }: { health: 'green' | 'amber' | 'red' }) {
  const variant = healthToVariant(health);
  const label = health === 'green' ? 'On Track' : health === 'amber' ? 'At Risk' : 'Off Track';
  return (
    <Badge variant={variant} className="text-xs gap-1.5">
      <StatusIndicator variant={variant === 'destructive' ? 'error' : variant} size="sm" />
      {label}
    </Badge>
  );
}

type SortKey = 'grossMarginActual' | 'grossProfitActual' | 'revenueActual' | 'projectName';
type SortDir = 'asc' | 'desc';

// ─────────────────────────────────────────────────────────────────────────────
// Side Sheet — Project Profitability Detail
// ─────────────────────────────────────────────────────────────────────────────

interface SideSheetProps {
  project: ProjectProfitabilityData | null;
  open: boolean;
  onClose: () => void;
}

function ProfitabilitySideSheet({ project, open, onClose }: SideSheetProps) {
  const navigate = useNavigate();

  if (!project) return null;
  const p = project.profitability;

  const gpMethodLabel = GP_METHOD_LABELS[getFinanceSettings().gpCalculationMethod] ?? 'Simple Margin';
  const isNegativeGP = p.grossProfit.actual < 0;

  // ── Financial table row ─────────────────────────────────────────────────────
  function FinRow({
    label,
    budget,
    actual,
    variance,
    varianceIsCost = false,
    bold = false,
    isMargRow = false,
    dimmed = false,
  }: {
    label: string;
    budget: number;
    actual: number;
    variance: number;
    varianceIsCost?: boolean;
    bold?: boolean;
    isMargRow?: boolean;
    dimmed?: boolean;
  }) {
    const varClass = varianceIsCost ? costVarianceClass(variance) : revenueVarianceClass(variance);
    const varStr = isMargRow ? formatPP(variance) : formatVariance(variance);
    const pctStr = isMargRow
      ? '—'
      : budget !== 0
        ? `${variance >= 0 ? '+' : ''}${((variance / Math.abs(budget)) * 100).toFixed(1)}%`
        : '—';

    return (
      <TableRow className={bold ? 'bg-muted/30 font-semibold' : undefined}>
        <TableCell className={`text-sm py-2 ${dimmed ? 'text-muted-foreground' : ''} ${bold ? 'font-semibold' : ''}`}>
          {label}
        </TableCell>
        <TableCell className={`text-right tabular-nums text-sm py-2 ${dimmed ? 'text-muted-foreground' : ''}`}>
          <SensitiveData>
            {isMargRow ? formatPercent(budget) : formatCurrency(budget)}
          </SensitiveData>
        </TableCell>
        <TableCell className={`text-right tabular-nums text-sm py-2 ${isNegativeGP && label === 'Gross Profit' ? 'text-destructive font-semibold' : dimmed ? 'text-muted-foreground' : ''}`}>
          <SensitiveData>
            {isMargRow ? formatPercent(actual) : formatCurrency(actual)}
          </SensitiveData>
        </TableCell>
        <TableCell className={`text-right tabular-nums text-sm py-2 ${varClass}`}>
          <SensitiveData>{varStr}</SensitiveData>
        </TableCell>
        <TableCell className={`text-right tabular-nums text-sm py-2 ${isMargRow ? 'text-muted-foreground' : varClass}`}>
          {pctStr}
        </TableCell>
      </TableRow>
    );
  }

  // ── Section divider row ──────────────────────────────────────────────────────
  function FinSectionHeader({ label }: { label: string }) {
    return (
      <TableRow className="bg-muted/50 hover:bg-muted/50">
        <TableCell colSpan={5} className="text-xs font-semibold uppercase tracking-widest text-muted-foreground py-2 pl-3">
          {label}
        </TableCell>
      </TableRow>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg flex flex-col p-0 overflow-hidden">

        {/* ── Header ─────────────────────────────────────────────────────────── */}
        <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <div className="flex items-start justify-between gap-3 pr-6">
            <div className="min-w-0">
              <SheetTitle className="text-base leading-snug truncate">
                {project.projectName}
              </SheetTitle>
              <SheetDescription asChild>
                <div className="mt-1.5 space-y-1 text-sm">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Badge variant="outline" className="font-mono text-xs shrink-0">
                      {project.projectCode}
                    </Badge>
                    <ProjectTypeBadge type={project.projectType} />
                    <HealthBadge health={project.health} />
                  </div>
                  <div className="text-muted-foreground">
                    <span className="font-medium text-foreground">Client:</span> {project.clientName}
                  </div>
                  <div className="text-muted-foreground">
                    <span className="font-medium text-foreground">PM:</span> {project.projectManager}
                  </div>
                </div>
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        {/* ── Scrollable body ─────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          {/* Loss warning banner */}
          {isNegativeGP && (
            <div className="flex items-center gap-3 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3">
              <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />
              <p className="text-sm font-medium text-destructive">
                This project is currently running at a loss.
              </p>
            </div>
          )}

          {/* ── Financial table ──────────────────────────────────────────────── */}
          <div className="rounded-lg border bg-card overflow-hidden">
            <Table disableScroll>
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  <TableHead className="text-xs font-medium text-muted-foreground uppercase tracking-wide py-2 w-full" />
                  <TableHead className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wide py-2 whitespace-nowrap">Budget</TableHead>
                  <TableHead className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wide py-2 whitespace-nowrap">Actual</TableHead>
                  <TableHead className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wide py-2 whitespace-nowrap">Var £</TableHead>
                  <TableHead className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wide py-2 whitespace-nowrap">Var %</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Revenue */}
                <FinSectionHeader label="Income" />
                <FinRow
                  label="Revenue"
                  budget={p.revenue.budget}
                  actual={p.revenue.actual}
                  variance={p.revenue.variance}
                />

                {/* Direct Costs */}
                <FinSectionHeader label="Costs" />
                <FinRow
                  label="Direct Costs"
                  budget={p.directCosts.budget}
                  actual={p.directCosts.actual}
                  variance={p.directCosts.variance}
                  varianceIsCost
                />

                {/* Gross Profit block */}
                <FinSectionHeader label="Gross Profit" />
                <FinRow
                  label="Gross Profit"
                  budget={p.grossProfit.budget}
                  actual={p.grossProfit.actual}
                  variance={p.grossProfit.variance}
                  bold
                />
                <FinRow
                  label="Gross Margin %"
                  budget={p.grossMargin.budget}
                  actual={p.grossMargin.actual}
                  variance={p.grossMargin.variance}
                  isMargRow
                  dimmed
                />

                {/* Overhead */}
                <FinSectionHeader label="Overhead" />
                <FinRow
                  label="Overhead Allocation"
                  budget={p.overhead.budget}
                  actual={p.overhead.actual}
                  variance={0}
                  dimmed
                />

                {/* Net Profit block */}
                <FinSectionHeader label="Net Profit" />
                <FinRow
                  label="Net Profit"
                  budget={p.netProfit.budget}
                  actual={p.netProfit.actual}
                  variance={p.netProfit.variance}
                  bold
                />
                <FinRow
                  label="Net Margin %"
                  budget={p.netMargin.budget}
                  actual={p.netMargin.actual}
                  variance={p.netMargin.variance}
                  isMargRow
                  dimmed
                />
              </TableBody>
            </Table>
          </div>

          {/* GP Method note */}
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="flex items-center gap-1 cursor-default">
                    <Info className="h-3 w-3" />
                    GP calculated using <span className="font-medium text-foreground">{gpMethodLabel}</span> method
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  GP calculation method is configured in General Settings and applies to all financial reports.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

        </div>

        {/* ── Footer ──────────────────────────────────────────────────────────── */}
        <SheetFooter className="px-6 py-4 border-t bg-muted/30 shrink-0 flex-row justify-between gap-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => {
              onClose();
              navigate(`${ROUTES.PROJECT_PL_REPORT}?reportProjectId=${project.projectId}`);
            }}
          >
            <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
            View P&L Report
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => {
              onClose();
              navigate(ROUTES.PROJECT_DETAILS(project.projectId));
            }}
          >
            <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
            Open Project
          </Button>
        </SheetFooter>

      </SheetContent>
    </Sheet>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────

export function ProfitabilityReportPage() {
  const [searchParamsHook] = useSearchParams();
  const initReportProjectId = searchParamsHook.get('reportProjectId');

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ProjectProfitabilityData[]>([]);
  const [summary, setSummary] = useState<PortfolioSummaryDto | null>(null);
  const [pagination, setPagination] = useState<PaginationMetadata | null>(null);
  const [filterOptions, setFilterOptions] = useState<ProfitabilityFilterOptions>({ projectManagers: [], statuses: [] });

  const [pmFilter, setPmFilter] = useState<string>('all');
  const [pmComboboxOpen, setPmComboboxOpen] = useState(false);
  const [billableFilter, setBillableFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>(DEFAULT_STATUS_FILTER);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('grossMarginActual');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [columnSorts, setColumnSorts] = useState<ColumnSort[]>([{ columnId: 'grossMarginActual', direction: 'desc' }]);

  // Deep-link: fetch project code for search filter
  useEffect(() => {
    if (initReportProjectId) {
      import('@/lib/data-service').then(({ fetchProjectById }) => {
        fetchProjectById(Number(initReportProjectId)).then((p) => {
          if (p) {
            const code = (p as any).code || (p as any).name || '';
            setSearchInput(code);
            setDebouncedSearch(code);
          }
        }).catch(() => { /* ignore */ });
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce user search input
  useEffect(() => {
    const handler = setTimeout(() => {
      const trimmed = searchInput.trim();
      setDebouncedSearch(trimmed || null);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchInput]);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [isExporting, setIsExporting] = useState(false);

  const { handleApiError } = useErrorHandler({ useToast: true });
  const { currentCompany } = useCompany();

  const thresholds = useMemo(() => getBudgetHealthThresholds(), []);
  const financeSettings = useMemo(() => getFinanceSettings(), []);
  const amberThreshold = thresholds.marginAmberThreshold ?? 25;
  const redThreshold = thresholds.marginRedThreshold ?? 15;

  // Map sort key to backend sort param
  const sortByParam = useMemo((): string => {
    switch (sortKey) {
      case 'projectName': return 'projectName';
      case 'revenueActual': return 'revenueActual';
      case 'grossProfitActual': return 'grossProfitActual';
      case 'grossMarginActual': return 'grossMarginActual';
      default: return 'grossMarginActual';
    }
  }, [sortKey]);

  // Build fetch params
  const fetchParams = useMemo((): ProfitabilityReportParams => ({
    page: currentPage,
    pageSize,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    pm: pmFilter !== 'all' ? pmFilter : undefined,
    billable: billableFilter !== 'all' ? billableFilter : undefined,
    search: debouncedSearch || undefined,
    sortBy: sortByParam,
    sortOrder: sortDir,
  }), [currentPage, pageSize, statusFilter, pmFilter, billableFilter, debouncedSearch, sortByParam, sortDir]);

  // Load data whenever params change
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const result = await fetchProfitabilityDataPaged(fetchParams);
        if (cancelled) return;
        setData(result.projects);
        setSummary(result.portfolioSummary);
        setPagination(result.pagination);
        setFilterOptions(result.filterOptions);
      } catch (err) {
        if (!cancelled) {
          handleApiError(err, '/api/reports/profitability', 'GET');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [fetchParams, handleApiError, currentCompany?.id]);

  // Reset page when filters change
  const prevFilterKey = useRef('');
  const filterKey = `${statusFilter}|${pmFilter}|${billableFilter}`;
  if (filterKey !== prevFilterKey.current) {
    prevFilterKey.current = filterKey;
    if (currentPage !== 1) setCurrentPage(1);
  }

  // Toggle sort — also resets to page 1
  function handleSort(col: SortKey) {
    if (col === sortKey) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(col);
      setSortDir('desc');
    }
    setCurrentPage(1);
  }

  const handleColumnSortChange = useCallback((sorts: ColumnSort[]) => {
    setColumnSorts(sorts);
    if (sorts.length > 0) {
      setSortKey(sorts[0].columnId as SortKey);
      setSortDir(sorts[0].direction as SortDir);
    }
    setCurrentPage(1);
  }, []);

  // Pagination handlers
  const handlePageChange = useCallback((page: number) => setCurrentPage(page), []);
  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  // ── Export to Excel ──────────────────────────────────────────────────────
  const handleExportExcel = useCallback(async () => {
    if (isExporting) return;
    setIsExporting(true);
    toast.info('Export started — your download will begin shortly.');

    try {
      // Fetch ALL pages for the current filter/sort by requesting a very large page
      const allResult = await fetchProfitabilityDataPaged({
        ...fetchParams,
        page: 1,
        pageSize: 50000,
      });
      await exportProfitabilityToExcel(allResult.projects);
      toast.success(`Export complete — ${allResult.projects.length} projects downloaded.`);
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, [isExporting, fetchParams]);

  // Summary values from server
  const totalGPActual = summary?.totalActualGP ?? 0;
  const portfolioGPPct = summary?.portfolioGPPercent ?? 0;
  const belowThresholdCount = summary?.projectsBelowAmberThreshold ?? 0;
  const totalFilteredProjects = summary?.totalProjects ?? 0;

  const activeFilterCount = (pmFilter !== 'all' ? 1 : 0) + (statusFilter !== 'all' ? 1 : 0) + (billableFilter !== 'all' ? 1 : 0) + (searchInput.trim() ? 1 : 0);

  const selectedProject = data.find(d => d.projectId === selectedProjectId) ?? null;
  const gpMethodLabel = GP_METHOD_LABELS[financeSettings.gpCalculationMethod] ?? 'Simple Margin';

  // ── Column definitions ──────────────────────────────────────────────────
  const columns: ColumnDef<ProjectProfitabilityData>[] = useMemo(() => [
    {
      id: 'health',
      header: 'Health',
      cell: ({ row }) => <HealthBadge health={row.original.health} />,
    },
    {
      id: 'projectName',
      header: 'Project',
      enableSorting: true,
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.projectName}</div>
          <div className="text-xs text-muted-foreground font-mono">{row.original.projectCode}</div>
        </div>
      ),
    },
    {
      id: 'clientName',
      header: 'Client',
      cell: ({ row }) => <span className="text-sm">{row.original.clientName}</span>,
    },
    {
      id: 'projectManager',
      header: 'PM',
      cell: ({ row }) => <span className="text-sm">{row.original.projectManager}</span>,
    },
    {
      id: 'projectType',
      header: 'Type',
      cell: ({ row }) => <ProjectTypeBadge type={row.original.projectType} />,
    },
    {
      id: 'billable',
      header: 'Billable',
      cell: ({ row }) => (row.original.billable ?? true)
        ? <Badge variant="outline" className="text-xs text-emerald-700 bg-emerald-50 border-emerald-200">Billable</Badge>
        : <Badge variant="outline" className="text-xs text-muted-foreground">Non-billable</Badge>,
    },
    {
      id: 'revenueActual',
      header: () => <span className="block text-right">Revenue (Act)</span>,
      enableSorting: true,
      cell: ({ row }) => (
        <div className="text-right">
          <SensitiveData>{formatCurrency(row.original.profitability.revenue.actual)}</SensitiveData>
        </div>
      ),
    },
    {
      id: 'budgetGP',
      header: () => <span className="block text-right">Budget GP £</span>,
      cell: ({ row }) => (
        <div className="text-right">
          <SensitiveData>{formatCurrency(row.original.profitability.grossProfit.budget)}</SensitiveData>
        </div>
      ),
    },
    {
      id: 'grossProfitActual',
      header: () => <span className="block text-right">Actual GP £</span>,
      enableSorting: true,
      cell: ({ row }) => {
        const isNeg = row.original.profitability.grossProfit.actual < 0;
        return (
          <div className="text-right">
            <SensitiveData>
              {isNeg ? (
                <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 bg-error-50 text-error-700 font-semibold text-sm">
                  <TrendingDown className="h-3.5 w-3.5" />
                  {formatCurrency(row.original.profitability.grossProfit.actual)}
                </span>
              ) : formatCurrency(row.original.profitability.grossProfit.actual)}
            </SensitiveData>
          </div>
        );
      },
    },
    {
      id: 'budgetGPPct',
      header: () => <span className="block text-right">Budget GP%</span>,
      cell: ({ row }) => (
        <div className="text-right text-muted-foreground">
          <SensitiveData>{formatPercent(row.original.profitability.grossMargin.budget)}</SensitiveData>
        </div>
      ),
    },
    {
      id: 'grossMarginActual',
      header: () => <span className="block text-right">Actual GP%</span>,
      enableSorting: true,
      cell: ({ row }) => (
        <div className="text-right">
          <SensitiveData>
            <RAGBadge
              margin={row.original.profitability.grossMargin.actual}
              amberThreshold={amberThreshold}
              redThreshold={redThreshold}
            />
          </SensitiveData>
        </div>
      ),
    },
    {
      id: 'gpVariance',
      header: () => <span className="block text-right">GP Variance £</span>,
      cell: ({ row }) => (
        <div className="text-right">
          <SensitiveData>
            <span className={revenueVarianceClass(row.original.profitability.grossProfit.variance)}>
              {formatVariance(row.original.profitability.grossProfit.variance)}
            </span>
          </SensitiveData>
        </div>
      ),
    },
    {
      id: 'gpVariancePP',
      header: () => <span className="block text-right">GP Variance</span>,
      cell: ({ row }) => {
        const gpVariancePP = row.original.profitability.grossMargin.variance;
        return (
          <div className="text-right">
            <SensitiveData>
              <span className={revenueVarianceClass(gpVariancePP)}>
                {formatPP(gpVariancePP)}
              </span>
            </SensitiveData>
          </div>
        );
      },
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [amberThreshold, redThreshold]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <PageHeaderBlock
          title="Profitability Analysis"
          subtitle="Portfolio-level gross profit and margin analysis across all projects"
          size={20}
        />
        <div className="flex items-center gap-1.5">
          <span className="text-sm text-muted-foreground">GP Method:</span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="secondary" className="text-xs cursor-default gap-1">
                  {gpMethodLabel}
                  <Info className="h-3 w-3" />
                </Badge>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                GP calculation method is set in General Settings and applies to all financial reports.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Card wrapping filters, summary cards and table */}
      <Card>

      {/* Filter row + GP Method badge */}
      <div className="flex flex-wrap items-center gap-3 p-6 pb-4" data-tour="profitability-filters">
        {/* Search */}
        <div className="relative w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search project code or name..."
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            className="h-8 pl-9 pr-9 text-sm"
          />
          {searchInput && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSearchInput('')}
              className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>

        {/* Status filter */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground whitespace-nowrap">Status:</span>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-8 w-44 text-sm">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUS_FILTER_OPTIONS.map(s => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* PM filter */}
        <Popover open={pmComboboxOpen} onOpenChange={setPmComboboxOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" role="combobox" className="h-8 w-48 justify-between text-sm font-normal">
              <span className="truncate">{pmFilter === 'all' ? 'All PMs' : pmFilter}</span>
              <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-56 p-0" align="start">
            <Command>
              <CommandInput placeholder="Search project managers..." className="h-8" />
              <CommandList>
                <CommandEmpty>No project managers found.</CommandEmpty>
                <CommandGroup>
                  <CommandItem value="all" onSelect={() => { setPmFilter('all'); setPmComboboxOpen(false); }}>
                    <Check className={`mr-2 h-4 w-4 ${pmFilter === 'all' ? 'opacity-100' : 'opacity-0'}`} />
                    All PMs
                  </CommandItem>
                  {filterOptions.projectManagers.map(pm => (
                    <CommandItem key={pm} value={pm} onSelect={() => { setPmFilter(pm); setPmComboboxOpen(false); }}>
                      <Check className={`mr-2 h-4 w-4 ${pmFilter === pm ? 'opacity-100' : 'opacity-0'}`} />
                      {pm}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {/* Billable filter */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground whitespace-nowrap">Billable:</span>
          <Select value={billableFilter} onValueChange={setBillableFilter}>
            <SelectTrigger className="h-8 w-36 text-sm">
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="billable">Billable</SelectItem>
              <SelectItem value="non-billable">Non-billable</SelectItem>
            </SelectContent>
          </Select>
        </div>



        {/* Export to Excel — right-aligned */}
        <div className="ml-auto">
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9"
            onClick={handleExportExcel}
            disabled={isExporting || totalFilteredProjects === 0}
            title="Export to Excel"
          >
            {isExporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      {/* Summary Cards — 5 cards */}
      <div className="px-6 pb-4">
      <div className="grid gap-4 md:grid-cols-5">
        <CompactKpiCard
          title="Portfolio GP"
          value={<SensitiveData>{formatCurrency(totalGPActual)}</SensitiveData>}
          description={`across ${totalFilteredProjects} project${totalFilteredProjects !== 1 ? 's' : ''}`}
          icon={TrendingUp}
          accentVariant={totalGPActual > 0 ? 'success' : totalGPActual < 0 ? 'error' : 'info'}
          className="shadow-none"
        />
        <CompactKpiCard
          title="Portfolio GP%"
          value={<SensitiveData>{formatPercent(portfolioGPPct)}</SensitiveData>}
          description="weighted average margin"
          icon={Percent}
          accentVariant={
            portfolioGPPct >= amberThreshold ? 'success'
            : portfolioGPPct >= redThreshold ? 'warning'
            : 'error'
          }
          className="shadow-none"
        />
        <CompactKpiCard
          title="Highest Margin"
          value={
            summary?.highestMarginProjectName
              ? <SensitiveData>{formatPercent(summary.highestMarginValue)}</SensitiveData>
              : '—'
          }
          description={summary?.highestMarginProjectName ?? '—'}
          icon={ChevronUp}
          accentVariant="success"
          className="shadow-none"
        />
        <CompactKpiCard
          title="Lowest Margin"
          value={
            summary?.lowestMarginProjectName
              ? <SensitiveData>{formatPercent(summary.lowestMarginValue)}</SensitiveData>
              : '—'
          }
          description={summary?.lowestMarginProjectName ?? '—'}
          icon={ChevronDown}
          accentVariant={
            !summary?.lowestMarginProjectName ? 'info'
            : summary.lowestMarginValue < redThreshold ? 'error'
            : summary.lowestMarginValue < amberThreshold ? 'warning'
            : 'success'
          }
          className="shadow-none"
        />
        <CompactKpiCard
          title="Below Threshold"
          value={belowThresholdCount}
          description={`below amber (${amberThreshold}%)`}
          icon={TriangleAlert}
          accentVariant={belowThresholdCount > 0 ? 'error' : 'success'}
          className="shadow-none"
        />
      </div>
      </div>

      {/* Project Table */}
      <CardContent className="p-0" data-tour="profitability-table">
        {loading ? (
          <div className="space-y-2 p-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-10 rounded-lg" />
            ))}
          </div>
        ) : data.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <AlertTriangle className="h-8 w-8 text-muted-foreground mb-3" />
            <p className="text-muted-foreground text-sm">
              No projects match the selected filters.{' '}
              {activeFilterCount > 0 && (
                <button
                  className="underline hover:no-underline"
                  onClick={() => { setPmFilter('all'); setStatusFilter('all'); setBillableFilter('all'); setSearchInput(''); }}
                >
                  Clear filters
                </button>
              )}
            </p>
          </div>
        ) : (
          <DataTableEnhanced
            columns={columns}
            data={data}
            columnSorts={columnSorts}
            onColumnSortChange={handleColumnSortChange}
            onRowClick={(row) => setSelectedProjectId(row.projectId)}
            getRowId={row => String(row.projectId)}
            className="rounded-none border-0 border-t cursor-pointer"
            paginationContent={
              pagination && pagination.totalItems > 0 ? (
                <PaginationControls
                  pagination={pagination}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  showPageSizeSelector
                />
              ) : undefined
            }
          />
        )}
      </CardContent>
      </Card>


      {/* Side Sheet */}
      <ProfitabilitySideSheet
        project={selectedProject}
        open={selectedProjectId !== null}
        onClose={() => setSelectedProjectId(null)}
      />
    </div>
  );
}
